/**
 * Table order tax calculator.
 * Calculates taxes for table orders, handling sent items (from POS) separately from new items (from cart).
 */

import {
  calculateOrderTax,
  calculateItemTaxItems,
  calculateTaxPerUnit,
  getPerUnitDiscount
} from "./taxCalculator";
import { setTaxRound } from "./utils";

export function calculateTableOrderTax(cartItems, storeConfig) {
  const {
    taxRound = "",
    isStoreTaxInclusive = false,
    taxRates = []
  } = storeConfig;
  const useCartItems = cartItems.length > 0;
  const activeItems = useCartItems ? cartItems : [];

  const getActiveModifiers = (item) =>
    useCartItems ? item.selectedModifiers || [] : item.modifiers || [];

  const normalizeModifiers = (mods) =>
    mods.map((mod) => ({ price: mod.price || 0 }));

  const ensureLocalId = (item) => {
    if (item.local_id) return item.local_id;
    if (item.cartId) {
      item.local_id = item.cartId;
      return item.local_id;
    }
    const generatedId = `${item.id}-${Date.now()}`;
    item.local_id = generatedId;
    return generatedId;
  };

  const resolveTaxRateValue = (taxItem) => {
    if (
      typeof taxItem.tax_rate_value === "number" &&
      !Number.isNaN(taxItem.tax_rate_value)
    ) {
      return taxItem.tax_rate_value;
    }

    const taxRateId = taxItem.product?.tax_rates?.[0];
    if (!taxRateId) {
      return 0;
    }

    const matchedRate = taxRates.find((tr) => tr.id === taxRateId);
    const result = matchedRate ? matchedRate.rate : 0;
    return result;
  };

  const subtotal =
    activeItems.reduce((sum, item) => {
      const modifiers = getActiveModifiers(item);
      const modTotal = modifiers.reduce(
        (mSum, mod) => mSum + (mod.price || 0),
        0
      );

      if (
        item.isSent === true &&
        item.grand_total !== undefined &&
        item.grand_total !== null
      ) {
        let itemTax = 0;
        if (
          item.tax_items &&
          Array.isArray(item.tax_items) &&
          item.tax_items.length > 0
        ) {
          itemTax = item.tax_items.reduce((taxSum, taxItem) => {
            return taxSum + (taxItem.tax_total || 0);
          }, 0);
        } else if (item.tax_total !== undefined && item.tax_total !== null) {
          itemTax = item.tax_total;
        } else if (item.tax !== undefined && item.tax !== null) {
          itemTax = item.tax * item.quantity;
        }

        // Calculate item subtotal from grand total
        // If inclusive: Subtotal = GrandTotal
        // If exclusive: Subtotal = GrandTotal - Tax
        let itemSubtotal = 0;
        if (isStoreTaxInclusive) {
          itemSubtotal = item.grand_total;
        } else {
          itemSubtotal = item.grand_total - itemTax;
        }
        return sum + itemSubtotal;
      }

      const itemDiscount = item.discount || 0;
      const wholeDiscount = item.wholeDiscount || 0;
      const itemSubtotal =
        (item.price + modTotal) * item.quantity - itemDiscount - wholeDiscount;
      return sum + itemSubtotal;
    }, 0) || 0;
  let totalTax = 0;
  const itemsWithTax = [...activeItems];

  if (taxRound === "order_level_distributed") {
    // First, ensure all synthetic rates are in the taxRates array
    activeItems.forEach((item) => {
      if (item.tax_rate_value != null && item.tax_rate_value > 0) {
        const syntheticId = `synthetic_${item.tax_rate_value}`;
        const existsInTaxRates = taxRates.find((tr) => tr.id === syntheticId);

        if (!existsInTaxRates) {
          const syntheticRate = {
            id: syntheticId,
            rate: item.tax_rate_value,
            name: `Tax ${item.tax_rate_value}%`
          };
          taxRates.push(syntheticRate);
        }
      }
    });

    activeItems.forEach((item, index) => {
      if (
        item.isSent === true &&
        item.tax_items &&
        Array.isArray(item.tax_items) &&
        item.tax_items.length > 0
      ) {
        itemsWithTax[index].tax_items = item.tax_items;
        return;
      }

      let productWithTaxRates = item.product;

      // If product doesn't have tax_rates, create it
      if (
        !productWithTaxRates ||
        !productWithTaxRates.tax_rates ||
        productWithTaxRates.tax_rates.length === 0
      ) {
        let itemTaxRates = item.tax_rates || [];

        // If item has tax_rate_value but no tax_rates, try to find matching rate
        if (
          itemTaxRates.length === 0 &&
          item.tax_rate_value != null &&
          taxRates.length > 0
        ) {
          // First look for synthetic rate
          const syntheticId = `synthetic_${item.tax_rate_value}`;
          const syntheticRate = taxRates.find((tr) => tr.id === syntheticId);

          if (syntheticRate) {
            itemTaxRates = [syntheticId];
          } else {
            // If synthetic doesn't exist, look for real rate
            const matchingTaxRate = taxRates.find(
              (tr) => Math.abs(tr.rate - item.tax_rate_value) < 0.01
            );
            if (matchingTaxRate) {
              itemTaxRates = [matchingTaxRate.id];
            }
          }
        }

        if (itemTaxRates.length > 0) {
          productWithTaxRates = {
            id: item.id,
            tax_rates: itemTaxRates
          };
        }
      }

      if (
        productWithTaxRates &&
        productWithTaxRates.tax_rates &&
        productWithTaxRates.tax_rates.length > 0
      ) {
        const modifiers = normalizeModifiers(getActiveModifiers(item));
        const normalizedItem = {
          ...item,
          modifiers,
          local_id: ensureLocalId(item)
        };

        itemsWithTax[index].tax_items = calculateItemTaxItems(
          normalizedItem,
          productWithTaxRates,
          taxRates,
          isStoreTaxInclusive,
          taxRound
        );

        itemsWithTax[index].product = productWithTaxRates;
      }
    });

    const sentItems = activeItems.filter((item) => {
      return item.isSent === true;
    });

    const sentItemsTax = sentItems.reduce((sum, item) => {
      if (
        item.tax_items &&
        Array.isArray(item.tax_items) &&
        item.tax_items.length > 0
      ) {
        const taxFromItems = item.tax_items.reduce((taxSum, taxItem) => {
          return taxSum + (taxItem.tax_total || 0);
        }, 0);
        return sum + taxFromItems;
      }
      if (item.tax_total !== undefined && item.tax_total !== null) {
        return sum + item.tax_total;
      }
      if (item.tax !== undefined && item.tax !== null) {
        return sum + item.tax * item.quantity;
      }
      return sum;
    }, 0);

    const newItemsForTax = activeItems.filter((item) => item.isSent !== true);

    let newItemsTax = 0;
    if (newItemsForTax.length > 0) {
      const order = {
        items: newItemsForTax.map((item) => {
          const itemIndex = activeItems.findIndex((ai) => ai === item);
          const itemWithTax = itemsWithTax[itemIndex] || item;

          let productWithTaxRates = item.product;
          if (
            !productWithTaxRates ||
            !productWithTaxRates.tax_rates ||
            productWithTaxRates.tax_rates.length === 0
          ) {
            let itemTaxRates = item.tax_rates || [];

            if (
              itemTaxRates.length === 0 &&
              item.tax_rate_value != null &&
              taxRates.length > 0
            ) {
              const matchingTaxRate = taxRates.find(
                (tr) => Math.abs(tr.rate - item.tax_rate_value) < 0.01
              );
              if (matchingTaxRate) {
                itemTaxRates = [matchingTaxRate.id];
              }
            }

            if (itemTaxRates.length > 0) {
              productWithTaxRates = {
                id: item.id,
                tax_rates: itemTaxRates
              };
            } else {
              productWithTaxRates = {};
            }
          }

          return {
            ...item,
            modifiers: normalizeModifiers(getActiveModifiers(item)),
            product: productWithTaxRates,
            tax_items: itemWithTax?.tax_items || item.tax_items || [],
            local_id: ensureLocalId(item)
          };
        })
      };

      newItemsTax = calculateOrderTax(
        order,
        taxRates,
        isStoreTaxInclusive,
        taxRound
      );
    }

    totalTax = sentItemsTax + newItemsTax;

    itemsWithTax.forEach((item) => {
      if (item.isSent === true) {
        if (item.tax_items && item.tax_items.length > 0) {
          item.tax = item.tax_items.reduce(
            (sum, taxItem) => sum + (taxItem.tax_value_item || 0),
            0
          );
        } else if (item.tax !== undefined) {
          return;
        } else {
          item.tax = 0;
        }
        return;
      }

      if (item.tax_items && item.tax_items.length > 0) {
        item.tax = item.tax_items.reduce(
          (sum, taxItem) => sum + (taxItem.tax_value_item || 0),
          0
        );
      } else {
        item.tax = 0;
      }
    });
  } else {
    activeItems.forEach((taxItem, index) => {
      if (taxItem.isSent === true && taxItem.tax !== undefined) {
        return;
      }

      const modifiers = getActiveModifiers(taxItem);
      const taxRateValue = resolveTaxRateValue(taxItem);

      if (!taxRateValue || taxRateValue <= 0) {
        itemsWithTax[index].tax = 0;
        return;
      }

      itemsWithTax[index].tax_rate_value = taxRateValue;

      // Use unified tax calculation formula
      const taxPerUnit = calculateTaxPerUnit({
        price: taxItem.price,
        modifiers: modifiers,
        itemDiscount: getPerUnitDiscount(taxItem.discount, taxItem.quantity),
        wholeDiscount: getPerUnitDiscount(
          taxItem.wholeDiscount,
          taxItem.quantity
        ),
        taxRateValue: taxRateValue,
        isTaxInclusive: isStoreTaxInclusive
      });

      itemsWithTax[index].tax = setTaxRound(taxRound, taxPerUnit);
    });

    const calculatedTax = activeItems.reduce((acc, current, idx) => {
      const itemWithTax = itemsWithTax[idx];
      if (
        current.isSent === true &&
        (current.tax !== undefined ||
          current.tax_total !== undefined ||
          (current.tax_items && current.tax_items.length > 0))
      ) {
        if (
          current.tax_items &&
          Array.isArray(current.tax_items) &&
          current.tax_items.length > 0
        ) {
          const taxFromItems = current.tax_items.reduce((taxSum, taxItem) => {
            return taxSum + (taxItem.tax_total || 0);
          }, 0);
          return acc + taxFromItems;
        }
        if (current.tax_total !== undefined && current.tax_total !== null) {
          return acc + current.tax_total;
        }
        if (current.tax !== undefined && current.tax !== null) {
          return acc + current.tax * current.quantity;
        }
        return acc;
      } else {
        return (
          acc + (itemWithTax.tax > 0 ? current.quantity * itemWithTax.tax : 0)
        );
      }
    }, 0);

    totalTax = setTaxRound(taxRound, calculatedTax);
  }

  return { totalTax, itemsWithTax, subtotal };
}
